import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { Search, MapPin, Briefcase, Clock, DollarSign, Building2, Filter, X, Upload, MessageCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { INDUSTRY_NAMES as INDUSTRIES } from "../lib/industries";
import ShareJob from "../components/ShareJob";
const EMPLOYMENT = [
  { v: "full_time", l: "Full-time" },
  { v: "contract", l: "Contract" },
  { v: "part_time", l: "Part-time" },
  { v: "internship", l: "Internship" },
];

export default function Jobs() {
  const [params, setParams] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    q: params.get("q") || "",
    location: params.get("location") || "",
    industry: params.get("industry") || "",
    employment_type: "",
    experience_min: "",
    salary_min: "",
  });
  const [mobileFilters, setMobileFilters] = useState(false);

  const fetchJobs = async (f = filters) => {
    setLoading(true);
    const p = {};
    Object.entries(f).forEach(([k, v]) => { if (v) p[k] = v; });
    const r = await api.get("/jobs", { params: p });
    setJobs(r.data);
    setLoading(false);
  };

  const urlIndustry = params.get("industry") || "";
  useEffect(() => {
    const f = { ...filters, industry: urlIndustry };
    setFilters(f);
    fetchJobs(f);
    /* eslint-disable-next-line */
  }, [urlIndustry]);

  const syncParams = (f) => setParams(Object.fromEntries(["q", "location", "industry"].filter(k => f[k]).map(k => [k, f[k]])));

  const submit = () => {
    if (filters.industry === urlIndustry) fetchJobs();
    syncParams(filters);
    setMobileFilters(false);
  };

  const pickIndustry = (i) => {
    const f = { ...filters, industry: filters.industry === i ? "" : i };
    setFilters(f);
    syncParams(f);
  };

  const clear = () => {
    const f = { q: "", location: "", industry: "", employment_type: "", experience_min: "", salary_min: "" };
    setFilters(f);
    setParams({});
    fetchJobs(f);
  };

  return (
    <div data-testid="jobs-page">
      <section className="bg-[#0F2A4A] text-white py-16">
        <div className="ae-container">
          <div className="ae-overline mb-3">Job Portal</div>
          <h1 className="text-3xl md:text-5xl font-light">Find your <span className="font-serif italic text-[#C5A059]">next role</span></h1>
          <p className="mt-3 text-white/70" data-testid="jobs-count">
            {jobs.length} open position{jobs.length === 1 ? "" : "s"}{urlIndustry ? <> in <span className="text-[#C5A059] font-medium">{urlIndustry}</span></> : " across India"}
          </p>
          {urlIndustry && (
            <button onClick={() => pickIndustry(urlIndustry)} data-testid="jobs-industry-chip"
              className="mt-4 inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 px-4 py-1.5 text-xs transition-colors">
              Industry: {urlIndustry} <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </section>

      <section className="py-8 bg-white sticky top-20 z-30 border-b border-slate-100">
        <div className="ae-container flex flex-col md:flex-row gap-3">
          <div className="flex-1 flex items-center gap-2 px-4 rounded-xl border border-slate-200 bg-slate-50">
            <Search className="w-4 h-4 text-slate-400" />
            <input value={filters.q} onChange={e => setFilters({ ...filters, q: e.target.value })}
              onKeyDown={e => e.key === "Enter" && submit()}
              data-testid="jobs-search-q"
              placeholder="Search title, skill, company" className="flex-1 py-3 bg-transparent outline-none text-sm" />
          </div>
          <div className="flex-1 flex items-center gap-2 px-4 rounded-xl border border-slate-200 bg-slate-50">
            <MapPin className="w-4 h-4 text-slate-400" />
            <input value={filters.location} onChange={e => setFilters({ ...filters, location: e.target.value })}
              onKeyDown={e => e.key === "Enter" && submit()}
              data-testid="jobs-search-loc"
              placeholder="Location" className="flex-1 py-3 bg-transparent outline-none text-sm" />
          </div>
          <button onClick={submit} className="ae-btn-primary text-sm" data-testid="jobs-search-submit">Search</button>
          <button onClick={() => setMobileFilters(true)} className="md:hidden ae-btn-outline text-sm flex items-center gap-2">
            <Filter className="w-4 h-4" /> Filters
          </button>
        </div>
      </section>

      <section className="py-12 bg-[#F8FAFC]">
        <div className="ae-container grid lg:grid-cols-12 gap-8">
          {/* Filters */}
          <aside className={`lg:col-span-3 ${mobileFilters ? "fixed inset-0 bg-white z-50 p-6 overflow-y-auto lg:relative lg:p-0 lg:bg-transparent" : "hidden lg:block"}`}>
            <div className="ae-card p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-heading font-semibold text-[#0F2A4A]">Filters</h3>
                <button onClick={clear} className="text-xs text-[#C5A059] font-medium" data-testid="filters-clear">Clear all</button>
                <button onClick={() => setMobileFilters(false)} className="lg:hidden"><X className="w-5 h-5" /></button>
              </div>

              <FilterGroup label="Industry">
                {INDUSTRIES.map(i => (
                  <button key={i} onClick={() => pickIndustry(i)}
                    data-testid={`filter-industry-${i}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${filters.industry === i ? "bg-[#0F2A4A] text-white border-[#0F2A4A]" : "bg-white text-slate-700 border-slate-200 hover:border-[#C5A059]"}`}>{i}</button>
                ))}
              </FilterGroup>

              <FilterGroup label="Employment Type">
                {EMPLOYMENT.map(e => (
                  <button key={e.v} onClick={() => setFilters({ ...filters, employment_type: filters.employment_type === e.v ? "" : e.v })}
                    data-testid={`filter-emp-${e.v}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${filters.employment_type === e.v ? "bg-[#0F2A4A] text-white border-[#0F2A4A]" : "bg-white text-slate-700 border-slate-200 hover:border-[#C5A059]"}`}>{e.l}</button>
                ))}
              </FilterGroup>

              <FilterGroup label="Min Experience (yrs)">
                <input type="number" min="0" value={filters.experience_min} onChange={e => setFilters({ ...filters, experience_min: e.target.value })}
                  data-testid="filter-exp"
                  className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none focus:border-[#C5A059]" />
              </FilterGroup>

              <FilterGroup label="Min Salary (₹ p.a.)">
                <input type="number" min="0" value={filters.salary_min} onChange={e => setFilters({ ...filters, salary_min: e.target.value })}
                  data-testid="filter-salary"
                  className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none focus:border-[#C5A059]" />
              </FilterGroup>

              <button onClick={submit} className="ae-btn-primary w-full mt-4 text-sm" data-testid="filters-apply">Apply Filters</button>
            </div>
          </aside>

          {/* Results */}
          <div className="lg:col-span-9 space-y-4">
            {loading ? (
              <div className="ae-card p-12 text-center text-slate-500">Loading jobs…</div>
            ) : jobs.length === 0 ? (
              <div className="ae-card p-12 text-center">
                <Briefcase className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                <p className="text-slate-500" data-testid="jobs-empty">
                  {urlIndustry ? `No open positions in ${urlIndustry} right now.` : "No jobs match your filters. Try broadening your search."}
                </p>
                <p className="text-sm text-slate-400 mt-2">Submit your CV below and we'll reach out when a matching role opens up.</p>
              </div>
            ) : jobs.map(j => (
              <Link to={`/jobs/${j.id}`} key={j.id} className="ae-card p-6 flex flex-col md:flex-row md:items-center justify-between gap-4 block" data-testid={`job-card-${j.id}`}>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-12 h-12 rounded-lg bg-[#0F2A4A]/5 flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-[#0F2A4A]" />
                    </div>
                    <div>
                      <h3 className="font-heading font-medium text-[#0F2A4A] text-lg">{j.title}</h3>
                      <div className="text-sm text-slate-500">{j.company_name}</div>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-3 text-xs text-slate-600 mt-3">
                    <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {j.location}</span>
                    <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {j.experience_min}-{j.experience_max} yrs</span>
                    {j.salary_min && <span className="flex items-center gap-1"><DollarSign className="w-3.5 h-3.5" /> ₹{(j.salary_min/100000).toFixed(1)}-{(j.salary_max/100000).toFixed(1)} LPA</span>}
                  </div>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {(j.skills || []).slice(0, 4).map(s => (
                      <span key={s} className="text-xs px-2 py-1 rounded-full bg-[#C5A059]/10 text-[#0F2A4A] border border-[#C5A059]/20">{s}</span>
                    ))}
                    <span className="text-xs px-2 py-1 rounded-full bg-[#0F2A4A] text-white">{j.industry}</span>
                  </div>
                </div>
                <div className="flex gap-2 items-center">
                  <ShareJob job={j} label="" testid={`job-share-${j.id}`}
                    triggerClassName="w-11 h-11 rounded-xl border border-slate-200 text-slate-500 hover:border-[#C5A059] hover:text-[#C5A059] inline-flex items-center justify-center transition-colors" />
                  <span className="ae-btn-primary text-sm whitespace-nowrap">View & Apply</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CvSubmitSection />
    </div>
  );
}

function CvSubmitSection() {
  const [f, setF] = React.useState({ name: "", phone: "", email: "", current_location: "", experience: "", preferred_job: "" });
  const [cv, setCv] = React.useState(null);
  const [submitted, setSubmitted] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!cv) { toast.info("Please attach your CV (PDF or DOC)"); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(f).forEach(([k, v]) => fd.append(k, v || ""));
      fd.append("cv_file", cv);
      await (await import("../lib/api")).api.post("/cv-submit", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSubmitted(true);
    } catch { toast.error("Something went wrong. Please try again."); }
    finally { setSubmitting(false); }
  };

  return (
    <section className="py-20 bg-white" data-testid="cv-submit-section">
      <div className="ae-container">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Image side */}
          <div className="order-2 lg:order-1">
            <div className="relative rounded-2xl overflow-hidden shadow-xl group">
              <img
                src="https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=1200&auto=format&fit=crop&q=80"
                alt="A professional male and female Indian job seeker discussing career opportunities in a modern corporate office"
                className="w-full h-[440px] object-cover transition-transform duration-[6000ms] ease-out group-hover:scale-105"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0F2A4A]/40 via-[#0F2A4A]/0 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <div className="inline-flex items-center gap-2 bg-white/95 backdrop-blur rounded-full px-4 py-2 text-xs font-medium text-[#0F2A4A]">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                  Your profile stays confidential
                </div>
              </div>
            </div>
          </div>

          {/* Text + Form */}
          <div className="order-1 lg:order-2">
            <div className="ae-overline mb-3">Can't Find The Right Job?</div>
            <h2 className="text-3xl md:text-4xl font-medium text-[#0F2A4A] leading-tight">
              Submit Your CV & Let Us Help You <span className="font-serif italic text-[#C5A059]">Find the Right Opportunity</span>
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Submit your CV and our recruitment team will consider your profile for suitable future opportunities.
            </p>

            {submitted ? (
              <div className="mt-8 ae-card p-8 text-center" data-testid="cv-success">
                <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto mb-3" />
                <h3 className="font-heading text-xl font-medium text-[#0F2A4A] mb-2">Thanks for sharing your CV.</h3>
                <p className="text-sm text-slate-600">Our recruitment team will review your profile and reach out when we have a role matching your skills and experience.</p>
              </div>
            ) : (
              <>
                <form onSubmit={submit} className="mt-6 ae-card p-6 space-y-3" data-testid="cv-form">
                  <div className="grid md:grid-cols-2 gap-3">
                    <Inp label="Full Name" val={f.name} onChange={v => setF({ ...f, name: v })} required tid="cv-name" />
                    <Inp label="Mobile / WhatsApp" val={f.phone} onChange={v => setF({ ...f, phone: v })} required tid="cv-phone" />
                    <Inp label="Email" type="email" val={f.email} onChange={v => setF({ ...f, email: v })} required tid="cv-email" />
                    <Inp label="Current Location" val={f.current_location} onChange={v => setF({ ...f, current_location: v })} tid="cv-loc" />
                    <Inp label="Experience" val={f.experience} onChange={v => setF({ ...f, experience: v })} placeholder="e.g., 3 years" tid="cv-exp" />
                    <Inp label="Preferred Job / Department" val={f.preferred_job} onChange={v => setF({ ...f, preferred_job: v })} placeholder="e.g., Sales, IT" tid="cv-pref" />
                  </div>
                  <label className="ae-btn-outline inline-flex items-center gap-2 cursor-pointer text-sm w-full justify-center" data-testid="cv-upload-label">
                    <Upload className="w-4 h-4" /> {cv ? cv.name : "Upload CV (PDF or DOC)"}
                    <input type="file" accept=".pdf,.doc,.docx" onChange={e => setCv(e.target.files[0])} className="hidden" data-testid="cv-input" required />
                  </label>
                  <div className="flex flex-col sm:flex-row gap-2 pt-1">
                    <button type="submit" disabled={submitting} className="ae-btn-primary text-sm flex-1" data-testid="cv-submit-btn">
                      {submitting ? "Submitting…" : "Submit Your CV"}
                    </button>
                    <a href="https://wa.me/919099042472?text=Hi%20Achievers%20Enterprise%2C%20I%27d%20like%20to%20share%20my%20CV%20for%20suitable%20opportunities."
                       target="_blank" rel="noreferrer" data-testid="cv-wa-btn"
                       className="border border-emerald-600 text-emerald-700 hover:bg-emerald-50 rounded-xl px-5 py-3 font-medium text-sm inline-flex items-center gap-2 justify-center">
                      <MessageCircle className="w-4 h-4" /> WhatsApp Us
                    </a>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

const Inp = ({ label, val, onChange, type = "text", tid, required, placeholder }) => (
  <div>
    <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-600 mb-1.5">{label}{required && " *"}</label>
    <input required={required} type={type} value={val} onChange={e => onChange(e.target.value)} placeholder={placeholder}
      data-testid={tid} className="w-full rounded-lg border border-slate-200 bg-slate-50 focus:bg-white focus:border-[#C5A059] outline-none px-3 py-2 text-sm" />
  </div>
);

function FilterGroup({ label, children }) {
  return (
    <div className="mb-5">
      <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">{label}</div>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}
